import type { NormalizedBridgeChartData, BridgeChartStudyCode, BridgeChartStudyState } from "../types/data";
import { studyDefinition } from "../studies/studyCatalog";
import type {
  BridgeChartPeriod,
  BridgeChartTimeframe,
  BridgeChartToolbarCommand,
  BridgeChartToolbarExtension,
  BridgeChartToolbarExtensionState,
  BridgeChartToolbarState,
  BridgeChartType
} from "../types/public";

export const bridgeChartPeriods: readonly BridgeChartPeriod[] = Object.freeze(["1M", "3M", "6M", "1Y", "2Y", "ALL"]);
export const bridgeChartTimeframes: readonly BridgeChartTimeframe[] = Object.freeze(["1D", "1W", "1M"]);
export const bridgeChartTypes: readonly BridgeChartType[] = Object.freeze(["candlestick"]);
export const bridgeChartToolbarExtensions: readonly BridgeChartToolbarExtensionState[] = Object.freeze(
  (["compare", "layout", "templates", "export"] as const).map(extension => Object.freeze({
    extension,
    available: false as const,
    reason: "NotImplemented" as const
  }))
);

export function createToolbarState(
  data: NormalizedBridgeChartData,
  studies: readonly BridgeChartStudyState[],
  current?: Pick<BridgeChartToolbarState, "settingsOpen" | "fullscreen" | "chartType">
): BridgeChartToolbarState {
  const symbol = String(data.symbol || data.metadata.symbol || "").trim();
  const timeframe = normalizeTimeframe(data.timeframe || data.metadata.timeframe);
  const period = normalizePeriod(data.metadata.period || "6M");
  const enabledIndicators = studies
    .filter(study => study.available && study.enabled)
    .map(study => study.code)
    .sort((left, right) => left.localeCompare(right));
  return Object.freeze({
    symbol,
    timeframe,
    period,
    chartType: normalizeChartType(current?.chartType || "candlestick"),
    enabledIndicators: Object.freeze(enabledIndicators),
    settingsOpen: current?.settingsOpen === true,
    fullscreen: current?.fullscreen === true,
    extensions: bridgeChartToolbarExtensions
  });
}

export function applyToolbarCommand(
  state: BridgeChartToolbarState,
  command: BridgeChartToolbarCommand
): BridgeChartToolbarState {
  switch (command.action) {
    case "setTimeframe":
      return withState(state, { timeframe: normalizeTimeframe(command.value) });
    case "setPeriod":
      return withState(state, { period: normalizePeriod(command.value) });
    case "setChartType":
      return withState(state, { chartType: normalizeChartType(command.value) });
    case "setIndicatorVisibility": {
      const code = normalizeStudyCode(command.code);
      const indicators = new Set(state.enabledIndicators);
      if (command.visible) indicators.add(code);
      else indicators.delete(code);
      return withState(state, { enabledIndicators: Object.freeze([...indicators].sort()) });
    }
    case "setSettingsOpen":
      return withState(state, { settingsOpen: command.value === true });
    case "setFullscreen":
      return withState(state, { fullscreen: command.value === true });
    case "compare":
    case "layout":
    case "templates":
    case "export":
      throw new Error(`BridgeChart toolbar extension '${command.action}' is unavailable.`);
  }
}

export function assertUnavailableExtension(extension: string): BridgeChartToolbarExtension {
  const normalized = extension.trim().toLowerCase();
  const match = bridgeChartToolbarExtensions.find(item => item.extension === normalized);
  if (!match) throw new TypeError(`BridgeChart toolbar extension '${extension}' is unknown.`);
  throw new Error(`BridgeChart toolbar extension '${match.extension}' is unavailable.`);
}

function withState(
  state: BridgeChartToolbarState,
  patch: Partial<Pick<BridgeChartToolbarState,
    "timeframe" | "period" | "chartType" | "enabledIndicators" | "settingsOpen" | "fullscreen">>
): BridgeChartToolbarState {
  return Object.freeze({ ...state, ...patch, extensions: bridgeChartToolbarExtensions });
}

function normalizeTimeframe(value: string): BridgeChartTimeframe {
  const normalized = String(value || "").trim().toUpperCase();
  if (!bridgeChartTimeframes.includes(normalized as BridgeChartTimeframe)) {
    throw new TypeError(`BridgeChart toolbar timeframe '${value}' is unsupported.`);
  }
  return normalized as BridgeChartTimeframe;
}

function normalizePeriod(value: string): BridgeChartPeriod {
  const normalized = String(value || "").trim().toUpperCase();
  if (!bridgeChartPeriods.includes(normalized as BridgeChartPeriod)) {
    throw new TypeError(`BridgeChart toolbar period '${value}' is unsupported.`);
  }
  return normalized as BridgeChartPeriod;
}

function normalizeChartType(value: string): BridgeChartType {
  const normalized = String(value || "").trim().toLowerCase();
  if (!bridgeChartTypes.includes(normalized as BridgeChartType)) {
    throw new TypeError(`BridgeChart chart type '${value}' is unavailable.`);
  }
  return normalized as BridgeChartType;
}

function normalizeStudyCode(value: string): BridgeChartStudyCode {
  const normalized = String(value || "").trim().toUpperCase() as BridgeChartStudyCode;
  if (!normalized || !studyDefinition(normalized)) {
    throw new TypeError(`BridgeChart toolbar indicator '${value}' is unsupported.`);
  }
  return normalized;
}
